import { TINTS } from "@/shared/ui/ContribCrystal/LiquidSphere";

/**
 * Пять сфер и их цвета — те же, что у шаров профиля (LiquidSphere.TINTS) и на
 * макетах (POINTS_BRIEF §5.1). «+N» всегда принадлежит одной сфере, поэтому он
 * рисуется её цветом.
 */
export type SphereKey = "contrib" | "workouts" | "social" | "referrals" | "learning";

export const SPHERE_ORDER: SphereKey[] = [
    "contrib",
    "workouts",
    "social",
    "referrals",
    "learning",
];

export const isSphereKey = (v: string): v is SphereKey =>
    (SPHERE_ORDER as string[]).includes(v);

const rgb = (c: number[]) => `${c[0]},${c[1]},${c[2]}`;

/**
 * Цвет текста суммы на тёмном фоне. Средний тон сферы (mid) на тёмном читается
 * хуже, поэтому число в чипе — на тон светлее, как на макетах (pts_lib.SPH.text).
 */
const TEXT: Record<SphereKey, string> = {
    contrib: "#A78BFA",
    workouts: "#F87171",
    social: "#60A5FA",
    referrals: "#34D399",
    learning: "#FBBF24",
};

/** CSS-переменные сферы для style={...}: --pp-deep / --pp-mid / --pp-light (r,g,b) и --pp-text. */
export const sphereVars = (key: SphereKey): Record<string, string> => {
    const t = TINTS[key];
    return {
        "--pp-deep": rgb(t.deep),
        "--pp-mid": rgb(t.mid),
        "--pp-light": rgb(t.light),
        "--pp-text": TEXT[key],
    };
};

/**
 * Разделитель тысяч — по языку страницы (телефон 11.10: английское окно шара писало «1 200 из
 * 90 000», а профиль и строки рядом — «1,200»): en, cn — «1,200», de — «1.200», es — «1200» и
 * «10.000» (Intl), ar — латинскими цифрами. Русский и без языка — «2 000» (неразрывный пробел),
 * как на макетах.
 */
const GROUP_LOCALE: Record<string, string> = {
    en: "en-US",
    de: "de-DE",
    es: "es-ES",
    cn: "zh-CN",
    zh: "zh-CN",
    ar: "ar-u-nu-latn",
};

/** «2 000» (ru и без языка) · «2,000» (en) · «2.000» (de): целое, с разделителем языка lang. */
export const formatPoints = (n: number, lang?: string | null): string => {
    const abs = Math.round(Math.abs(n));
    const locale = GROUP_LOCALE[String(lang || "").slice(0, 2).toLowerCase()];
    const s = locale
        ? new Intl.NumberFormat(locale, { maximumFractionDigits: 0 }).format(abs)
        : String(abs).replace(/\B(?=(\d{3})+(?!\d))/g, " ");
    return n < 0 ? `−${s}` : s;
};

/** «+2 000» («+2,000» по-английски). Ноль и отрицательное — без плюса. */
export const formatGain = (n: number, lang?: string | null): string =>
    n > 0 ? `+${formatPoints(n, lang)}` : formatPoints(n, lang);

/**
 * Сумма и «за что» одной строкой: хвост с запятой прилипает к сумме («+2 000, если друг
 * установит приложение»), без запятой — через пробел («+2 000 when a friend installs the
 * app», а не «+2 000when…»).
 */
export const joinAmountTail = (amount: string, tail?: string | null): string =>
    !tail ? amount : /^[,.;:，،]/.test(tail) ? `${amount}${tail}` : `${amount} ${tail}`;
